import { Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Observable, forkJoin } from 'rxjs';

import { TriggerStylesService } from '@services//styles/administration/machine/trigger/trigger-styles.service';
import { ArrayOperationService } from '@services/administration/array-operation/array-operation.service';
import { TriggerConfigurationService } from '@services/administration/machine/trigger/trigger-configuration.service';
import { LoadingIndicatorService } from '@services/loading-indicator/loading-indicator.service';
import { NotificationService } from '@services/notification/notification.service';
import { TableHeaderSortableService } from '@services/table-header-sortable/table-header-sortable.service';
import { UtilsService } from '@services/utils/utils.service';

import { SortEvent, TableHeaderSortableDirective } from '@shared/table-header-sortable/table-header-sortable.directive';

import { TriggerConfiguration } from '@models/adminstration/machine/trigger/trigger-configuration';
import { Machine } from '@models/machine/machine';

import {
  TriggerConfigurationEditComponent
} from '@components/administration/machine/machine-configuration/trigger/trigger-configuration/modals/trigger-configuration-edit/trigger-configuration-edit.component';
import { Role } from '@models/user/role';
import { User } from '@models/user/user';
import { ModalService } from '@services/modal/modal.service';

@Component({
    selector: 'scitis-admin-machine-trigger-configuration',
    templateUrl: './trigger-configuration.component.html',
    styleUrls: ['./trigger-configuration.component.scss'],
    standalone: false
})
export class TriggerConfigurationComponent implements OnInit {

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  machine: Machine = new Machine();

  isAddTriggerConfigurationButtonDisabled = true;
  triggerConfigurationList: TriggerConfiguration[] = [];
  isGapDetectionInProgress: any = {};
  isTriggerActivatable: any = {};
  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();
  private CURRENT_PAGE_NAME = 'TriggerConfigurationComponent';
  private defaultSortType: SortEvent = {column: 'name', direction: 'asc'};

  constructor(
      private triggerConfigurationService: TriggerConfigurationService,
      private loadingIndicator: LoadingIndicatorService,
      private translation: TranslateService,
      private notificationService: NotificationService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private arrayOperationService: ArrayOperationService,
      private modalService: ModalService,
      private triggerStylesService: TriggerStylesService,
      private utilsService: UtilsService) {
  }

  ngOnInit(): void {
    this.loadTriggerConfigs();
    this.isAddTriggerConfigurationButtonDisabled = !(this.userRole.isSuperAdminRole || this.userRole.isTechAdminRole || this.userRole.isAdminRole);
  }

  onColumnSort({column, direction}: SortEvent): void {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.triggerConfigurationList = this.tableHeaderSortableService.sortItems<TriggerConfiguration>(this.triggerConfigurationList, column, direction);
  }

  onAddTriggerConfiguration(): void {
    const modalRef = this.getModal();

    modalRef.result.then((triggerConfiguration: TriggerConfiguration) => {
      this.setGapDetectionInProgress(triggerConfiguration);
      this.triggerConfigurationList.push(triggerConfiguration);
      this.onColumnSort(this.defaultSortType);
    });
  }

  onDeleteTriggerConfiguration(triggerConfiguration: TriggerConfiguration): void {
    this.arrayOperationService.deleteItem(triggerConfiguration, this.triggerConfigurationList, 'id');
    this.onColumnSort(this.defaultSortType);
    this.loadTriggerConfigs();
  }

  onUpdatedTriggerConfiguration(updatedTriggerConfiguration?: TriggerConfiguration): void {
    if (updatedTriggerConfiguration && updatedTriggerConfiguration.id) {
      const index = this.triggerConfigurationList.findIndex(t => t.id === updatedTriggerConfiguration.id);
      if (index > -1) {
        this.triggerConfigurationList[index] = Object.assign(new TriggerConfiguration(), updatedTriggerConfiguration);
        this.triggerConfigurationList = [...this.triggerConfigurationList];
      }
      this.onColumnSort(this.defaultSortType);
      this.setGapDetectionInProgress(updatedTriggerConfiguration);
      return;
    }
    this.loadTriggerConfigs();
  }

  getBadgeCssClass(isActive: boolean) {
    return this.triggerStylesService.getTriggerConfigurationBadgeClass(isActive);
  }

  getCircleCssClass(isActive: boolean) {
    return this.triggerStylesService.getTriggerConfigurationCircleClass(isActive);
  }

  getActiveWord(isActive: boolean) {
    return this.translation.instant(this.triggerStylesService.getTriggerConfigurationActiveWord(isActive));
  }

  public getModal(): NgbModalRef {
    const modalRef = this.modalService.open(TriggerConfigurationEditComponent, {size: 'xl'});
    this.setModalInputParameters(modalRef);
    return modalRef;
  }

  getTriggerConfiguration(triggerConfiguration: TriggerConfiguration): TriggerConfiguration {
    return this.utilsService.getObjectDeepCopy(triggerConfiguration);
  }

  private loadTriggerConfigs(): void {
    if (this.machine.id) {
      this.loadingIndicator.show(this.translation.instant('Loading'));
      const observables = new Array<Observable<any>>();
      const triggerConfigurationObservable = this.triggerConfigurationService.triggerConfiguration(this.machine.id);
      const gapDetectionObservable = this.triggerConfigurationService.gapDetection(this.machine.id);
      observables.push(triggerConfigurationObservable);
      observables.push(gapDetectionObservable);

      forkJoin(observables).subscribe(results => {
        this.triggerConfigurationList = results[0];
        this.onColumnSort(this.defaultSortType);

        const deactivatedTriggers = results[1];
        for (const triggerConfiguration of this.triggerConfigurationList) {
          this.isGapDetectionInProgress[triggerConfiguration.id] = deactivatedTriggers.indexOf(triggerConfiguration.id) > -1;
          this.isTriggerActivatable[triggerConfiguration.id] = !!triggerConfiguration.triggerQueries;
        }
        this.loadingIndicator.hide();
      }, error => {
        console.log(error);
        this.loadingIndicator.hide();
        const errTxt = 'Error loading trigger configuration';
        this.notificationService.error(this.translation.instant(errTxt), error, this.CURRENT_PAGE_NAME, true);
      });
    }
  }

  private setGapDetectionInProgress(triggerConfiguration: TriggerConfiguration): void {
    if (this.machine.id) {
      this.loadingIndicator.show(this.translation.instant('Loading'));
      this.triggerConfigurationService.gapDetection(this.machine.id).subscribe(deactivatedTriggers => {
        this.isGapDetectionInProgress[triggerConfiguration.id] = deactivatedTriggers.indexOf(triggerConfiguration.id) > -1;
        this.isTriggerActivatable[triggerConfiguration.id] = !!triggerConfiguration.triggerQueries;
        this.loadingIndicator.hide();
      }, error => {
        console.log(error);
        this.loadingIndicator.hide();
      });
    }
  }

  private setModalInputParameters(modalRef: NgbModalRef) {
    modalRef.componentInstance.userRole = this.userRole;
    modalRef.componentInstance.machine = this.machine;
  }

}
